/**
 * feat-307-redesign-tenant-mgmt (spec D3) — the Admin root pane, shown when no
 * tenant is selected.
 *
 * Built ONLY from data the page already loads: the GET /api/tenant list and its
 * is_live / drip_enabled flags. The mockup's "Locations" total and "No
 * locations yet" list are left out — GET /api/tenant carries no location
 * count (F1), and inventing one is what D3/D4 forbid.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Tenant } from '../../../types/tenant';
import { cls, fmtDate } from './styles';
import { tenantPath } from './routes';

export interface RootPaneProps {
  tenants: Tenant[];
  /** Fail-closed Admin gate; the live/drip figures are Admin-only data. */
  showClientStatus: boolean;
}

export default function RootPane({ tenants, showClientStatus }: RootPaneProps) {
  const live = tenants.filter((t) => t.isLive).length;
  const drip = tenants.filter((t) => t.dripEnabled).length;
  const notLive = tenants
    .filter((t) => !t.isLive)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  const tiles: Array<[string, number]> = showClientStatus
    ? [
        ['Tenants', tenants.length],
        ['Live clients', live],
        ['Drip on', drip],
      ]
    : [['Tenants', tenants.length]];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="root-pane">
      <h2 className="font-display text-2xl font-semibold text-ink">All tenants</h2>
      <p className={`${cls.muted} mt-1`}>Pick a tenant in the tree to see its settings and locations.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        {tiles.map(([label, value]) => (
          <div key={label} className={`${cls.card} p-4`} data-testid={`root-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={cls.eyebrow}>{label}</div>
            <div className="font-display text-3xl font-semibold text-ink mt-1">{value}</div>
          </div>
        ))}
      </div>
      {showClientStatus && (
        <section className={`${cls.card} p-4 mt-6 max-w-xl`} data-testid="root-not-live">
          <h3 className={`${cls.h2} !text-base mb-2`}>Not live yet</h3>
          {notLive.length === 0 ? (
            <p className={cls.muted}>Every tenant is a live client.</p>
          ) : (
            <ul>
              {notLive.map((t) => (
                <li key={t.tenantId} className="border-t border-softBorder first:border-0">
                  <Link
                    to={tenantPath(t.tenantId)}
                    className="flex items-center justify-between gap-3 py-2 text-sm text-ink no-underline hover:text-coral"
                  >
                    <span className="font-semibold truncate">{t.tenantName}</span>
                    <span className="text-inkMuted text-xs whitespace-nowrap">Created {fmtDate(t.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
